import { Center, Loader } from "@mantine/core";
import type { User } from "@supabase/supabase-js";
import type * as React from "react";
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router";
import type { Capability } from "@contract/firm/capabilities";
import { MeProvider, useMe } from "./api/MeContext";
import { useSession, type SessionState } from "./auth/useSession";
import { AppLayout } from "./layout/AppLayout";
import { AccountPage, displayName } from "./pages/AccountPage";
import { ActivityPage } from "./pages/ActivityPage";
import { AdminConsentPage } from "./pages/AdminConsentPage";
import { DeletedPage } from "./pages/DeletedPage";
import { HomePage } from "./pages/HomePage";
import { MembersPage } from "./pages/MembersPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PlanBillingPage } from "./pages/PlanBillingPage";
import { SignInPage } from "./pages/SignInPage";
import { StandardsPage } from "./pages/StandardsPage";
import { LibraryPage } from "./pages/LibraryPage";

const Spinner = () => (
  <Center mih="100vh">
    <Loader />
  </Center>
);

/** Signed-out pages bounce a signed-in user onward (sign-up → onboarding). */
function PublicOnly({ session, to, children }: { session: SessionState; to: string; children: React.ReactElement }) {
  const [params] = useSearchParams();
  if (session.status === "loading") return <Spinner />;
  if (session.status === "signedIn") return <Navigate to={params.get("next") || to} replace />;
  return children;
}

function SignedIn({ session, children }: { session: SessionState; children: (user: User) => React.ReactElement }) {
  const location = useLocation();
  if (session.status === "loading") return <Spinner />;
  if (session.status === "signedOut") {
    const next = location.pathname === "/" ? "" : `?next=${encodeURIComponent(location.pathname)}`;
    return <Navigate to={`/sign-in${next}`} replace />;
  }
  return children(session.session.user);
}

/** Admin pages: hidden from members, and a typed URL goes Home (spec §10.2). */
function RequireCapability({ capability, children }: { capability: Capability; children: React.ReactElement }) {
  const { state } = useMe();
  if (state.status !== "ready") return null;
  return state.me.capabilities.includes(capability) ? children : <Navigate to="/" replace />;
}

export function App({ session: injected }: { session?: SessionState }) {
  const live = useSession();
  const session = injected ?? live;
  const email = session.status === "signedIn" ? session.session.user.email ?? "" : "";
  const userName = session.status === "signedIn" ? displayName(session.session.user) : null;
  return (
    <Routes>
      <Route path="/sign-in" element={<PublicOnly session={session} to="/"><SignInPage mode="sign-in" /></PublicOnly>} />
      <Route path="/sign-up" element={<PublicOnly session={session} to="/"><SignInPage mode="sign-up" /></PublicOnly>} />
      <Route path="/help/admin-consent" element={<AdminConsentPage />} />
      <Route path="/deleted" element={<DeletedPage />} />
      <Route
        element={
          <SignedIn session={session}>
            {() => (
              <MeProvider>
                <AppLayout email={email} />
              </MeProvider>
            )}
          </SignedIn>
        }
      >
        <Route index element={<HomePage email={email} userName={userName} />} />
        {/* The Welcome page became Home's Get Started (A8); invite emails and old links still
            land on /welcome. */}
        <Route path="/welcome" element={<Navigate to="/" replace />} />
        <Route path="/members" element={<RequireCapability capability="manage_members"><MembersPage /></RequireCapability>} />
        <Route path="/billing" element={<RequireCapability capability="manage_billing"><PlanBillingPage /></RequireCapability>} />
        <Route path="/standards" element={<RequireCapability capability="use_product"><StandardsPage /></RequireCapability>} />
        <Route path="/library" element={<RequireCapability capability="use_product"><LibraryPage /></RequireCapability>} />
        <Route path="/activity" element={<RequireCapability capability="view_activity"><ActivityPage /></RequireCapability>} />
        <Route
          path="/account"
          element={<SignedIn session={session}>{(user) => <AccountPage user={user} />}</SignedIn>}
        />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
